import { Link } from "@tanstack/react-router";
import { SiteNav } from "@/components/layout/SiteNav";
import { SiteFooter } from "@/components/layout/SiteFooter";
import { CaseStudyTOC } from "@/components/caseStudy/CaseStudyTOC";
import { EyebrowLabel } from "@/components/ui-ext/Badge";
import { getProject } from "@/content/portfolio";
import { INDUSTRIES } from "@/content/industries";
import { SERVICES } from "@/content/services";
import { locationVariantKey } from "@/content/imagery";
import { Picture } from "@/components/media/Picture";
import { BrandFigure } from "@/components/media/BrandFigure";
import { LocationMap } from "@/components/locations/LocationMap";
import { getNearbyLocations, type LocationPage } from "@/content/locations";
import { Breadcrumbs } from "@/components/shared/Breadcrumbs";
import { relatedBlogPosts } from "@/lib/relatedGuides";
import { SiloDiscovery } from "@/components/shared/SiloDiscovery";
import { siteConfig } from "@/config/site";


import {
  PriorityLinkCluster,
  hasPriorityCluster,
} from "@/components/locations/PriorityLinkCluster";
import { LocationMobileBar } from "@/components/locations/LocationMobileBar";

export function LocationPageTemplate({ location }: { location: LocationPage }) {
  const hasProof = location.localProof.length > 0;
  const nearby = getNearbyLocations(location.slug, 4);
  const founderAttribution =
    location.slug === siteConfig.contact.address.city.toLowerCase()
      ? `Led personally by founder ${siteConfig.founder.name} from our South ${siteConfig.contact.address.city} HQ (${siteConfig.contact.address.zip}) — every strategy, roadmap, and QA pass has his name on it.`
      : `Led personally by founder ${siteConfig.founder.name} from our South ${siteConfig.contact.address.city} HQ (${siteConfig.contact.address.zip}) — in-market strategy days and on-site work available across ${location.city}.`;

  const hasSpotlights = (location.neighborhoodSpotlights?.length ?? 0) > 0;
  const hasDeepDive = (location.deepDive?.length ?? 0) > 0;
  const hasPlaybooks = (location.industryPlaybooks?.length ?? 0) > 0;
  const rawToc: { id: string; label: string; show: boolean }[] = [
    { id: "snapshot", label: "Market snapshot", show: true },
    { id: "economics", label: "Economics", show: true },
    { id: "why-here", label: "Why us", show: true },
    { id: "coverage", label: "Coverage", show: true },
    { id: "neighborhoods", label: "Neighbourhoods", show: hasSpotlights },
    { id: "deep-dive", label: `${location.city} deep dive`, show: hasDeepDive },
    { id: "industry-playbooks", label: "Industry playbooks", show: hasPlaybooks },
    { id: "industries", label: "Industries", show: true },
    { id: "services", label: "Services", show: true },
    { id: "competitors", label: "Competitive set", show: true },
    { id: "proof", label: "Proof", show: hasProof },
    { id: "fit", label: "Fit", show: true },
    { id: "faq", label: "FAQ", show: true },
    { id: "connected", label: "Connected pages", show: hasPriorityCluster(location.slug) },
  ];
  const toc = rawToc
    .filter((t) => t.show)
    .map((t, i) => ({ id: t.id, label: `${String(i + 1).padStart(2, "0")} · ${t.label}` }));

  const proofProjects = location.localProof
    .map(getProject)
    .filter(Boolean) as NonNullable<ReturnType<typeof getProject>>[];

  const localIndustries = location.industriesLocal
    .map((li) => ({ ind: INDUSTRIES.find((i) => i.slug === li.slug), note: li.note }))
    .filter((x) => x.ind);

  const localServices = location.servicesLocal
    .map((slug) => SERVICES.find((s) => s.slug === slug))
    .filter(Boolean) as (typeof SERVICES)[number][];

  // NOTE: primary LocalBusiness/Breadcrumb/FAQ JSON-LD is emitted by the route's
  // head() via ldScripts(...) in src/routes/locations.$slug.tsx. Do not duplicate
  // it here — the previous inline block used a placeholder URL and shipped a
  // second, conflicting LocalBusiness node to crawlers.


  return (
    <div>
      <SiteNav />
      <main id="main" tabIndex={-1}>
        <div className="container-page pt-10">
          <Breadcrumbs 
            items={[
              { name: "Locations", path: "/locations/" },
              { name: `${location.city}, TX SEO`, path: `/seo-agency-${location.slug}/` }
            ]} 
          />
        </div>


        {/* HERO */}
        <section className="relative overflow-hidden border-b border-border">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-70"
            style={{ background: "var(--gradient-hero)" }}
          />
          <div className="container-page relative pt-12 pb-12 md:pt-16 md:pb-16">
            <div className="mt-4 grid lg:grid-cols-[1.4fr_1fr] gap-8 items-end">
              <div>
                <EyebrowLabel>{location.eyebrow}</EyebrowLabel>
                <h1 className="mt-5 font-display text-[2.15rem] leading-[1.08] sm:text-5xl sm:leading-[1.02] md:text-7xl text-balance">
                  {location.hero.headline}
                </h1>
                <p className="mt-5 max-w-2xl text-base sm:text-lg text-muted-foreground">
                  {location.hero.deck}
                </p>
                <div className="mt-8 flex flex-col sm:flex-row sm:flex-wrap gap-3">
                  <Link
                    to="/contact/"
                    search={(prev) => ({ ...prev, location: location.slug, service: "" })}
                    className="inline-flex min-h-12 items-center justify-center rounded-full bg-primary px-6 text-sm font-semibold text-primary-foreground"
                  >
                    Book a {location.city} strategy call →
                  </Link>
                  <a
                    href="#coverage"
                    className="inline-flex min-h-12 items-center justify-center rounded-full border border-border-strong px-6 text-sm hover:border-primary hover:text-primary"
                  >
                    See coverage
                  </a>
                </div>
              </div>
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden border border-border bg-surface-2">
                  <Picture
                    variantKey={locationVariantKey(location.slug)}
                    alt=""
                    width={1280}
                    height={800}
                    priority
                    sizes="(min-width: 1024px) 480px, (min-width: 640px) 60vw, 100vw"
                    imgClassName="w-full h-auto object-cover"
                  />
                </div>
                <div className="surface-card p-6">
                  <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                    At a glance
                  </div>
                  <ul className="mt-4 space-y-3">
                    {location.tldr.map((t) => (
                      <li key={t} className="flex items-start gap-3 text-sm">
                        <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="container-page py-12 grid gap-12 lg:grid-cols-[220px_1fr] items-start">
          <CaseStudyTOC items={toc} />

          <article className="max-w-3xl min-w-0">
            {/* Snapshot */}
            <section id="snapshot" className="scroll-mt-24">
              <EyebrowLabel>Market snapshot</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl leading-tight">
                How search actually behaves in {location.city}.
              </h2>
              <div className="mt-6 space-y-5">
                {location.marketSnapshot.map((p, i) => (
                  <p key={i} className="text-base md:text-lg leading-relaxed text-foreground/90">
                    {p}
                  </p>
                ))}
              </div>
              {location.slug === siteConfig.contact.address.city.toLowerCase() && (
                <div className="mt-8 p-6 rounded-2xl bg-surface-2 border border-primary/20">
                  <h3 className="font-display text-lg mb-2">Google Business Profile</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Visit our {siteConfig.contact.address.city} headquarters on Google Maps to see live results, reviews, and location-based performance signals.
                  </p>
                  <a 
                    href={siteConfig.social.yelp.replace("yelp.com/biz/", "maps.app.goo.gl/")} // Fallback logic or direct link needed if Yelp isn't GMB
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-primary text-sm font-semibold hover:underline"
                  >
                    View {siteConfig.contact.address.city} GMB Profile →
                  </a>
                </div>
              )}

              <BrandFigure
                className="mt-8"
                kind="map-pack"
                title={`Local Pack vs. organic behaviour in ${location.city}`}
              />
            </section>

            {/* Economics */}
            <section id="economics" className="scroll-mt-24 mt-8 border-t border-border pt-8">
              <EyebrowLabel>Metro economics</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">The numbers.</h2>
              <div className="mt-8 grid gap-4 md:grid-cols-3">
                {location.economics.map((e) => (
                  <div key={e.label} className="surface-card p-6">
                    <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                      {e.label}
                    </div>
                    <div className="mt-2 font-display text-3xl text-primary">{e.value}</div>
                    <div className="mt-2 text-xs text-muted-foreground">{e.note}</div>
                  </div>
                ))}
                {location.extraStats?.map((e) => (
                  <div key={e.label} className="surface-card p-6">
                    <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                      {e.label}
                    </div>
                    <div className="mt-2 font-display text-3xl text-primary">{e.value}</div>
                    <div className="mt-2 text-xs text-muted-foreground">{e.note}</div>
                  </div>
                ))}
              </div>
            </section>

            {/* Why here */}
            <section id="why-here" className="scroll-mt-24 mt-8 border-t border-border pt-8">
              <EyebrowLabel>Why Optimize Plus here</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                What we bring to a {location.city} programme.
              </h2>
              <ul className="mt-8 space-y-3">
                <li className="surface-card p-5 flex items-start gap-3 ring-1 ring-primary/30">
                  <span className="mt-1 text-primary text-xl leading-none">★</span>
                  <span className="text-sm md:text-base text-foreground/90">{founderAttribution}</span>
                </li>
                {location.whyHere.map((w) => (
                  <li key={w} className="surface-card p-5 flex items-start gap-3">
                    <span className="mt-1 text-primary text-xl leading-none">›</span>
                    <span className="text-sm md:text-base text-foreground/90">{w}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Coverage */}
            <section id="coverage" className="scroll-mt-24 mt-8 border-t border-border pt-8">
              <EyebrowLabel>Neighbourhood & ZIP coverage</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                The catchment we operate across.
              </h2>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {location.coverage.map((c) => (
                  <div key={c.name} className="surface-card p-5">
                    <div className="font-display text-lg">{c.name}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{c.note}</div>
                  </div>
                ))}
              </div>
              <BrandFigure
                className="mt-8"
                kind="coverage-map"
                title={`${location.city} service-area coverage and proximity radius`}
              />
              <LocationMap
                className="mt-8"
                city={location.city}
                region={location.region}
                latLng={location.latLng}
              />
            </section>

            {/* Neighborhood spotlights (optional) */}
            {location.neighborhoodSpotlights && location.neighborhoodSpotlights.length > 0 && (
              <section id="neighborhoods" className="scroll-mt-24 mt-10 border-t border-border pt-10">
                <EyebrowLabel>Neighbourhood spotlights</EyebrowLabel>
                <h2 className="mt-4 font-display text-3xl md:text-4xl">
                  How search actually behaves, ZIP by ZIP.
                </h2>
                <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
                  Local Packs in {location.city} vary meaningfully by neighbourhood. These are
                  the sub-markets we run distinct playbooks for.
                </p>
                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  {location.neighborhoodSpotlights.map((n) => {
                    const inner = (
                      <div id={`neighborhood-${n.slug}`} className="scroll-mt-24">
                        <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                          {n.zip}
                        </div>
                        <div className="mt-1 font-display text-lg">{n.name}</div>
                        <div className="mt-1 text-sm text-foreground/90">{n.headline}</div>
                        <p className="mt-3 text-sm text-muted-foreground">{n.body}</p>
                        {n.slug && location.slug === "austin" && (
                          <div className="mt-4 text-xs text-primary">
                            Read the full {n.name.split(" (")[0]} playbook →
                          </div>
                        )}
                      </div>
                    );
                    return n.slug && location.slug === "austin" ? (
                      <a
                        key={n.name}
                        href={`#neighborhood-${n.slug}`}
                        className="surface-card p-5 block hover:-translate-y-0.5 transition-transform"
                      >
                        {inner}
                      </a>
                    ) : (
                      <div key={n.name} className="surface-card p-5">
                        {inner}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Deep dive (optional) */}
            {location.deepDive && location.deepDive.length > 0 && (
              <section id="deep-dive" className="scroll-mt-24 mt-10 border-t border-border pt-10">
                <EyebrowLabel>{location.city} deep dive</EyebrowLabel>
                <h2 className="mt-4 font-display text-3xl md:text-4xl">
                  The local SEO reality most agencies miss.
                </h2>
                <div className="mt-8 space-y-10">
                  {location.deepDive.map((d) => (
                    <div key={d.heading}>
                      <h3 className="font-display text-2xl leading-tight text-balance">
                        {d.heading}
                      </h3>
                      <div className="mt-4 space-y-4">
                        {d.body.map((p, i) => (
                          <p key={i} className="text-base md:text-lg leading-relaxed text-foreground/90">
                            {p}
                          </p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Industry playbooks (optional) */}
            {location.industryPlaybooks && location.industryPlaybooks.length > 0 && (
              <section id="industry-playbooks" className="scroll-mt-24 mt-10 border-t border-border pt-10">
                <EyebrowLabel>Industry playbooks</EyebrowLabel>
                <h2 className="mt-4 font-display text-3xl md:text-4xl">
                  Local plays we run, by vertical.
                </h2>
                <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
                  Each vertical has its own {location.city} pattern. These are the ones we run
                  most often.
                </p>
                <div className="mt-8 grid gap-4 md:grid-cols-2">
                  {location.industryPlaybooks.map((ip) => (
                    <div key={ip.industry} className="surface-card p-6 flex flex-col group">
                      <div className="flex items-baseline justify-between gap-4 flex-wrap">
                        <h3 className="font-display text-xl group-hover:text-primary transition-colors">{ip.industry}</h3>
                      </div>
                      <p className="mt-2 text-sm text-muted-foreground flex-1">{ip.angle}</p>
                      <ul className="mt-4 space-y-2 mb-6">
                        {ip.plays.slice(0, 3).map((p) => (
                          <li key={p} className="flex items-start gap-2 text-sm">
                            <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                            <span>{p}</span>
                          </li>
                        ))}
                      </ul>
                      {ip.slug && (
                        <Link
                          to="/industries/$slug/"
                          params={{ slug: ip.slug }}
                          className="mt-auto pt-4 border-t border-border text-xs text-primary font-semibold flex items-center gap-1 hover:underline"
                        >
                          Full {ip.industry} Playbook <span aria-hidden>→</span>
                        </Link>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}



            {/* Industries */}
            <section id="industries" className="scroll-mt-24 mt-10 border-t border-border pt-10">
              <EyebrowLabel>Industries in {location.city}</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                Verticals we run here.
              </h2>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {localIndustries.map(({ ind, note }) => (
                  <Link
                    key={ind!.slug}
                    to="/industries/$slug/"
                    params={{ slug: ind!.slug }}
                    className="surface-card p-5 hover:-translate-y-0.5 transition-transform"
                  >
                    <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                      {ind!.eyebrow}
                    </div>
                    <div className="mt-1 font-display text-lg">{ind!.name}</div>
                    <div className="mt-2 text-sm text-muted-foreground line-clamp-2">{note}</div>
                  </Link>
                ))}
              </div>
            </section>

            {/* Services */}
            <section id="services" className="scroll-mt-24 mt-10 border-t border-border pt-10">
              <EyebrowLabel>Services in {location.city}</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                Disciplines we deploy here.
              </h2>
              <div className="mt-8 grid gap-4 sm:grid-cols-2">
                {localServices.map((s) => (
                  <Link
                    key={s.slug}
                    to="/services/$slug/"
                    params={{ slug: s.slug }}
                    className="surface-card p-5 hover:-translate-y-0.5 transition-transform"
                  >
                    <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                      {s.category}
                    </div>
                    <div className="mt-1 font-display text-lg">{s.title}</div>
                    <div className="mt-2 text-sm text-muted-foreground line-clamp-2">
                      {s.metaDescription}
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            {/* Competitors */}
            <section id="competitors" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>Competitive set</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                Who you're actually up against.
              </h2>
              <div className="mt-8 space-y-4">
                {location.competitors.map((c) => (
                  <div key={c.type} className="surface-card p-5">
                    <div className="font-display text-lg">{c.type}</div>
                    <p className="mt-2 text-sm text-muted-foreground">{c.note}</p>
                  </div>
                ))}
              </div>
              <BrandFigure
                className="mt-8"
                kind="rank-curve"
                title={`Typical ${location.city} visibility curve once the programme compounds`}
              />
            </section>

            {/* Proof */}
            {hasProof && (
              <section id="proof" className="scroll-mt-24 mt-16 border-t border-border pt-12">
                <EyebrowLabel>Local proof</EyebrowLabel>
                <h2 className="mt-4 font-display text-3xl md:text-4xl">
                  {location.city}-area engagements on file.
                </h2>
                <div className="mt-8 grid gap-6">
                  {proofProjects.map((p) => (
                    <Link
                      key={p.slug}
                      to="/portfolio/$slug/"
                      params={{ slug: p.slug }}
                      className="surface-card p-6 grid gap-4 sm:grid-cols-[1fr_auto] items-center hover:-translate-y-0.5 transition-transform"
                    >
                      <div>
                        <div className="text-[0.65rem] uppercase tracking-widest text-muted-foreground">
                          {p.industry} · {p.window}
                        </div>
                        <div className="mt-1 font-display text-xl">{p.client}</div>
                        <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                          {p.tagline}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="font-display text-3xl text-primary">
                          {p.metrics[0].value}
                        </div>
                        <div className="text-[0.65rem] uppercase tracking-wider text-muted-foreground">
                          {p.metrics[0].label}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* Fit */}
            <section id="fit" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>Is this for you?</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                We turn away work that isn't a fit.
              </h2>
              <div className="mt-8 grid gap-6 md:grid-cols-2">
                <div className="surface-card p-6">
                  <div className="text-primary text-xs uppercase tracking-widest">
                    This is for you if
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {location.fitFor.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-primary" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="surface-card p-6">
                  <div className="text-muted-foreground text-xs uppercase tracking-widest">
                    Not a fit if
                  </div>
                  <ul className="mt-4 space-y-2.5">
                    {location.notFor.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-muted-foreground" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section id="faq" className="scroll-mt-24 mt-16 border-t border-border pt-12">
              <EyebrowLabel>FAQ</EyebrowLabel>
              <h2 className="mt-4 font-display text-3xl md:text-4xl">
                Questions we get on every {location.city} call.
              </h2>
              <div className="mt-8 space-y-3">
                {location.faqs.map((f) => (
                  <details
                    key={f.q}
                    className="surface-card p-5 group open:ring-1 open:ring-primary/30"
                  >
                    <summary className="cursor-pointer list-none flex items-center justify-between gap-4">
                      <span className="font-medium text-foreground">{f.q}</span>
                      <span className="text-primary text-xl leading-none transition-transform group-open:rotate-45">
                        +
                      </span>
                    </summary>
                    <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
                      {f.a}
                    </p>
                  </details>
                ))}
              </div>
            </section>

            {/* Related posts */}
            {(() => {
              const posts = relatedBlogPosts(location.slug);
              if (!posts.length) return null;
              return (
                <section className="scroll-mt-24 mt-12 border-t border-border pt-10">
                  <EyebrowLabel>Related insights</EyebrowLabel>
                  <h2 className="mt-4 font-display text-3xl md:text-4xl">
                    Internal notes on {location.city} search.
                  </h2>
                  <div className="mt-8 grid gap-4 sm:grid-cols-2">
                    {posts.map((p) => (
                      <Link
                        key={p.slug}
                        to={"/blog/$slug/" as any}    
                        params={{ slug: p.slug } as any}
                        className="surface-card p-5 group hover:-translate-y-0.5 transition-transform"
                      >
                        <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                          {p.category}
                        </div>
                        <div className="mt-1 font-display text-lg group-hover:text-primary transition-colors">
                          {p.title}
                        </div>
                        <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                          {p.dek}
                        </p>
                      </Link>
                    ))}
                  </div>
                </section>
              );
            })()}

            {/* Structured internal links for all markets to reinforce silo */}
            {hasPriorityCluster(location.slug) ? (
              <PriorityLinkCluster slug={location.slug} />
            ) : (
              <SiloDiscovery 
                eyebrow="Market connectivity"
                heading={`Where ${location.city} fits in your Texas strategy.`}
                intro={`${location.city} is a key growth market within our Texas operational network. Our Central Texas HQ provides the methodology and team to scale your local footprint.`}
                links={[
                  {
                    to: "/locations/",
                    anchor: "View all Texas SEO markets",
                    note: "See our full coverage across the I-35 and Gulf Coast corridors."
                  },
                  {
                    to: "/services/local-seo/",
                    anchor: "Local SEO & Local Pack programmes",
                    note: `The core service used to dominate the Local Pack in ${location.city}.`
                  },
                  {
                    to: `/seo-agency-${siteConfig.contact.address.city.toLowerCase()}/`,
                    anchor: `${siteConfig.contact.address.city} HQ & strategy hub`,
                    note: "Direct coordination from our primary Central Texas office."
                  },
                  {
                    to: "/contact/",
                    anchor: "Book a Strategy Call",
                    note: `Map out your ${location.city} capture strategy with our team.`
                  }
                ]}
              />
            )}

            {/* Nearby markets */}
            {nearby.length > 0 && (
              <section className="scroll-mt-24 mt-16 border-t border-border pt-12">
                <EyebrowLabel>Nearby markets</EyebrowLabel>
                <h2 className="mt-4 font-display text-3xl md:text-4xl">
                  Other Texas markets we run from Austin.
                </h2>
                <p className="mt-3 text-muted-foreground max-w-2xl text-sm md:text-base">
                  {location.city} programmes often extend across the {location.region} corridor.
                  These are the neighbouring metros we operate in most frequently.
                </p>
                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  {nearby.map((n) => (
                    <Link
                      key={n.slug}
                      to="/seo-agency-{$slug}/"
                      params={{ slug: n.slug }}
                      className="surface-card p-5 hover:-translate-y-0.5 transition-transform"
                    >
                      <div className="text-[0.65rem] uppercase tracking-widest text-primary">
                        {n.region}
                      </div>
                      <div className="mt-1 font-display text-lg">{n.city}, TX</div>
                      <div className="mt-2 text-sm text-muted-foreground line-clamp-2">
                        {n.hero.deck}
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}

            {/* CTA */}
            <section className="mt-16">
              <div className="surface-card p-8 md:p-10">
                <EyebrowLabel>Next step</EyebrowLabel>
                <h2 className="mt-3 font-display text-3xl md:text-4xl">
                  Ready to run a proper {location.city} programme?
                </h2>
                <p className="mt-3 text-muted-foreground max-w-xl">
                  Book a free strategy call. We'll audit your current {location.city}{" "}
                  footprint and tell you honestly whether we're the right fit.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/contact/"
                    search={{ location: location.slug, service: "" }}
                    className="inline-flex rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground"
                  >
                    Book a strategy call →
                  </Link>
                  <Link
                    to="/locations/"
                    className="inline-flex rounded-full border border-border-strong px-6 py-3 text-sm hover:border-primary hover:text-primary"
                  >
                    All locations
                  </Link>
                </div>
              </div>
            </section>
          </article>
        </section>
      </main>
      <SiteFooter />
      <LocationMobileBar city={location.city} slug={location.slug} />
    </div>
  );
}
